
import { AdminAnalyticsService } from "../../../services/admin-analytics.service.js";

export const SettlementTab = async (container) => {
    // State
    let currentView = 'overview'; // overview | content | settlement
    let dateFilter = new Date().toISOString().split('T')[0];
    let platformFeePercent = 70; // Default Fee

    // --- Render Frame ---
    container.innerHTML = `
        <div class="flex flex-col gap-6 animate-fade-in pb-20">
            <!-- Header & Toolbar -->
            <div class="flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <span class="material-symbols-outlined text-green-600">payments</span> 
                        Settlement & Revenue
                    </h2>
                    <p class="text-xs text-gray-500">Amazon-grade Financial Integrity System</p>
                </div>

                <div class="flex bg-gray-100 p-1 rounded-lg">
                    <button class="px-4 py-2 text-sm font-bold rounded-md transition-all ${currentView === 'overview' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}" id="nav-overview">
                        Revenue Overview
                    </button>
                    <button class="px-4 py-2 text-sm font-bold rounded-md transition-all ${currentView === 'content' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}" id="nav-content">
                        Content Sales
                    </button>
                    <button class="px-4 py-2 text-sm font-bold rounded-md transition-all ${currentView === 'settlement' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}" id="nav-settlement">
                        Author Payout
                    </button>
                </div>

                <div class="flex items-center gap-3">
                    <!-- Fee Setting (Visible only in Settlement View) -->
                    <div id="fee-control" class="hidden flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-lg border border-gray-200">
                        <label class="text-xs font-bold text-gray-500">Platform Fee %</label>
                        <input type="number" id="input-fee" class="w-16 bg-white border border-gray-300 text-gray-900 text-sm rounded focus:ring-green-500 focus:border-green-500 block p-1 text-right" value="${platformFeePercent}" min="0" max="100">
                    </div>

                    <input type="date" id="date-picker" class="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2" value="${dateFilter}">
                    <button id="btn-refresh" class="bg-green-50 hover:bg-green-100 text-green-600 p-2 rounded-lg transition-colors" title="Refresh Data">
                        <span class="material-symbols-outlined text-[20px]">refresh</span>
                    </button>
                    <!-- Simulation Button (Dev Only) -->
                     <div class="flex gap-1">
                        <button id="btn-simulate" class="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-l-lg transition-colors text-xs font-bold shadow-md opacity-50 hover:opacity-100" title="Generate Mock Data">
                            Dev: Sim
                        </button>
                         <button id="btn-clear-mock" class="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-r-lg transition-colors text-xs font-bold shadow-md opacity-50 hover:opacity-100" title="Clear Mock Data">
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            <!-- Content Area -->
            <div id="analytics-content" class="min-h-[400px]">
                <!-- Injected via JS -->
                <div class="flex justify-center items-center h-64">
                    <span class="material-symbols-outlined animate-spin text-4xl text-gray-300">progress_activity</span>
                </div>
            </div>
        </div>
    `;

    // --- Logic ---

    // 1. Navigation Handlers
    const setView = (view) => {
        currentView = view;
        // Update Buttons
        ['overview', 'content', 'settlement'].forEach(v => {
            const btn = container.querySelector(`#nav-${v}`);
            if (v === currentView) {
                btn.className = 'px-4 py-2 text-sm font-bold rounded-md transition-all bg-white text-green-600 shadow-sm';
            } else {
                btn.className = 'px-4 py-2 text-sm font-bold rounded-md transition-all text-gray-500 hover:text-gray-700';
            }
        });

        // Toggle Fee Control Visibility
        const feeControl = container.querySelector('#fee-control');
        if (view === 'settlement') {
            feeControl.classList.remove('hidden');
        } else {
            feeControl.classList.add('hidden');
        }

        loadData();
    };

    container.querySelector('#nav-overview').onclick = () => setView('overview');
    container.querySelector('#nav-content').onclick = () => setView('content');
    container.querySelector('#nav-settlement').onclick = () => setView('settlement');

    container.querySelector('#btn-refresh').onclick = () => loadData();
    container.querySelector('#btn-simulate').onclick = async () => {
        if (confirm("⚠️ Generate MOCK DATA for verification?\n(This will overwrite today's stats with random values.)")) {
            const success = await AdminAnalyticsService.seedMockData();
            if (success) {
                alert("✅ Mock Data Generated! Refreshing...");
                loadData();
            } else {
                alert("❌ Failed to generate data. Check console.");
            }
        }
    };
    container.querySelector('#btn-clear-mock').onclick = async () => {
        if (confirm("⚠️ Clear Mock Data?\n(This will DELETE today's mock stats.)")) {
            const success = await AdminAnalyticsService.clearMockData();
            if (success) {
                alert("✅ Mock Data Cleared! Refreshing...");
                loadData();
            } else {
                alert("❌ Failed to clear data. Check console.");
            }
        }
    };
    container.querySelector('#date-picker').onchange = (e) => {
        dateFilter = e.target.value;
        loadData();
    };
    container.querySelector('#input-fee').onchange = (e) => {
        let val = parseInt(e.target.value);
        if (isNaN(val) || val < 0) val = 0;
        if (val > 100) val = 100;
        platformFeePercent = val;
        loadData(); // Reload calculation
    };

    // 2. Data Loading & Rendering
    const loadData = async () => {
        const contentDiv = container.querySelector('#analytics-content');
        contentDiv.innerHTML = `<div class="flex justify-center items-center h-64"><span class="material-symbols-outlined animate-spin text-4xl text-gray-300">progress_activity</span></div>`;

        try {
            if (currentView === 'overview') {
                const data = await AdminAnalyticsService.getOverviewStats();
                renderOverview(contentDiv, data);
            } else if (currentView === 'content') {
                const data = await AdminAnalyticsService.getSeriesReport(dateFilter);
                renderContentReport(contentDiv, data);
            } else if (currentView === 'settlement') {
                const data = await AdminAnalyticsService.getSettlementCandidates(dateFilter, platformFeePercent);
                renderSettlement(contentDiv, data);
            }
        } catch (e) {
            contentDiv.innerHTML = `<div class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">Error loading data: ${e.message}</div>`;
        }
    };

    // --- Renderers ---

    const renderOverview = (target, data) => {
        const { total_revenue_krw = 0, total_minted_stars = 0 } = data;

        target.innerHTML = `
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <!-- Revenue Card -->
                <div class="bg-gradient-to-br from-green-600 to-emerald-800 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden group">
                    <div class="absolute -right-4 -top-4 bg-white/10 w-32 h-32 rounded-full group-hover:scale-110 transition-transform"></div>
                    <div class="relative z-10">
                        <p class="text-green-100 text-sm font-bold uppercase mb-2 tracking-wider">Total Revenue (KRW)</p>
                        <h3 class="text-4xl font-black mb-4">₩ ${total_revenue_krw.toLocaleString()}</h3>
                         <div class="flex items-center gap-2">
                             <span class="text-xs bg-black/20 px-2 py-1 rounded backdrop-blur-sm">Today's Accumulation</span>
                             <span class="text-xs bg-green-500/80 px-2 py-1 rounded backdrop-blur-sm font-bold animate-pulse">Live</span>
                         </div>
                    </div>
                </div>

                <!-- Stars Consumed -->
                <div class="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm relative overflow-hidden flex flex-col justify-center">
                    <div class="absolute -right-8 -top-8 bg-yellow-400/5 w-40 h-40 rounded-full"></div>
                    <div class="relative z-10">
                        <p class="text-gray-500 text-sm font-bold uppercase mb-2">Stars Minted (Sold)</p>
                        <h3 class="text-4xl font-black text-gray-900 flex items-center gap-3">
                             ${total_minted_stars.toLocaleString()} <span class="text-lg text-yellow-500 font-bold bg-yellow-50 px-2 py-1 rounded">Stars</span>
                        </h3>
                         <p class="text-gray-400 text-xs mt-3">Reflects total stars purchased by users today.</p>
                    </div>
                </div>
            </div>

            <!-- Placeholder for Charts -->
             <div class="bg-white border border-gray-200 rounded-2xl p-8 min-h-[300px] flex flex-col items-center justify-center text-gray-400">
                <span class="material-symbols-outlined text-5xl mb-4 opacity-20">show_chart</span>
                <p class="text-sm font-medium">Revenue Charts will be available after 24h of data accumulation.</p>
            </div>
        `;
    };

    const renderContentReport = (target, list) => {
        if (!list || list.length === 0) {
            target.innerHTML = `
                <div class="bg-gray-50 rounded-xl p-12 text-center border border-gray-200">
                    <span class="material-symbols-outlined text-4xl text-gray-300 mb-2">folder_off</span>
                    <p class="text-gray-500 font-bold">No sales data found for this date.</p>
                </div>`;
            return;
        }

        // Sort by Total Revenue
        const sorted = list.sort((a, b) => (b.total_revenue_star || 0) - (a.total_revenue_star || 0));

        target.innerHTML = `
            <div class="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <table class="w-full text-left border-collapse">
                    <thead class="bg-gray-50 text-xs text-gray-500 uppercase font-bold">
                        <tr>
                            <th class="px-6 py-4 border-b border-gray-200">Rank</th>
                            <th class="px-6 py-4 border-b border-gray-200">Series Title</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right">Novel (Star)</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right">Toon (Star)</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right">Total Revenue</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-sm">
                        ${sorted.map((item, idx) => `
                            <tr class="hover:bg-green-50/30 transition-colors group">
                                <td class="px-6 py-4 font-mono text-gray-400 w-16">${idx + 1}</td>
                                <td class="px-6 py-4">
                                    <div class="font-bold text-gray-900">${item.series_title || 'Unknown Series'}</div>
                                    <div class="text-[10px] text-gray-400 font-mono">${item.series_id}</div>
                                </td>
                                <td class="px-6 py-4 text-right font-mono text-gray-600">${(item.novel_revenue || 0).toLocaleString()}</td>
                                <td class="px-6 py-4 text-right font-mono text-gray-600">${(item.toon_revenue || 0).toLocaleString()}</td>
                                <td class="px-6 py-4 text-right">
                                    <span class="bg-green-100 text-green-700 px-2 py-1 rounded font-bold font-mono">
                                        ${(item.total_revenue_star || 0).toLocaleString()}
                                    </span>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;
    };

    const renderSettlement = (target, list) => {
        if (!list || list.length === 0) {
            target.innerHTML = `
                <div class="bg-gray-50 rounded-xl p-12 text-center border border-gray-200">
                    <span class="material-symbols-outlined text-4xl text-gray-300 mb-2">payments</span>
                    <p class="text-gray-500 font-bold">No settlement candidates found for this date.</p>
                </div>`;
            return;
        }

        target.innerHTML = `
            <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex justify-between items-center">
                <div class="flex items-center gap-3">
                    <div class="bg-amber-100 p-2 rounded text-amber-600"><span class="material-symbols-outlined">info</span></div>
                    <div>
                    <div>
                        <h4 class="text-amber-900 font-bold text-sm">Settlement Policy (Dynamic)</h4>
                        <p class="text-amber-800 text-xs">Platform Fee: ${platformFeePercent}% | Withholding Tax: 3.3%</p>
                    </div>
                    </div>
                </div>
                <button class="bg-white border border-amber-300 text-amber-700 px-4 py-2 rounded-lg text-xs font-bold hover:bg-amber-100 shadow-sm" onclick="alert('Download Excel feature coming soon')">
                    Download Excel
                </button>
            </div>

            <div class="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <table class="w-full text-left">
                    <thead class="bg-gray-50 text-xs text-gray-500 uppercase font-bold">
                        <tr>
                            <th class="px-6 py-4 border-b border-gray-200">Author</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right">Total Revenue (Gross)</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right text-red-400">Fee (${platformFeePercent}%)</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right text-red-400">Tax (3.3%)</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-right bg-green-50 text-green-900">Final Payout</th>
                            <th class="px-6 py-4 border-b border-gray-200 text-center">Status</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-sm">
                        ${list.map(item => `
                            <tr class="hover:bg-gray-50 transition-colors">
                                <td class="px-6 py-4">
                                    <div class="font-bold text-gray-900">${item.name}</div>
                                    <div class="text-[10px] text-gray-400 font-mono">${item.id}</div>
                                </td>
                                <td class="px-6 py-4 text-right font-mono font-bold">${item.total_revenue.toLocaleString()}</td>
                                <td class="px-6 py-4 text-right font-mono text-red-400">-${item.platform_fee.toLocaleString()}</td>
                                <td class="px-6 py-4 text-right font-mono text-red-400">-${item.tax.toLocaleString()}</td>
                                <td class="px-6 py-4 text-right font-mono font-bold text-green-700 bg-green-50/50">
                                    ₩ ${item.final_payout.toLocaleString()}
                                </td>
                                <td class="px-6 py-4 text-center">
                                    <span class="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold bg-blue-100 text-blue-700">
                                        <span class="w-1.5 h-1.5 rounded-full bg-blue-500"></span> Pending
                                    </span>
                                </td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>
        `;
    };

    // Initial Load
    loadData();
};
